import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// SortableRow component for dnd-kit
function SortableRow({ item, index, getBestTraderInfo, itemsEnData, removeItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const vendorInfo = getBestTraderInfo(item);
  const itemName = item.translatedName || itemsEnData[item.name] || item.name;

  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <td className="td-icon"><img src={item.iconLink || ''} alt="" onError={(e) => e.target.style.display='none'} /></td>
      <td className="item-name">{itemName}</td>
      <td className="td-vendor">{vendorInfo.name}</td>
      <td className="td-price">{vendorInfo.price}</td>
      <td className="td-action"><button className="remove-btn" onClick={() => removeItem(item.id)}>Remove</button></td>
    </tr>
  );
}


function TrackerContainer() {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [itemsData, setItemsData] = useState({});
  const [tradersData, setTradersData] = useState({});
  const [itemsEnData, setItemsEnData] = useState({});
  const [tradersEnData, setTradersEnData] = useState({});
  const [currentSort, setCurrentSort] = useState({ column: null, asc: true });

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Load from localStorage immediately when the component mounts
    const storedSavedItems = JSON.parse(localStorage.getItem('tarkovSavedItems')) || [];
    setSavedItems(storedSavedItems);

    const initializeApp = async () => {
      try {
        const [itemsRes, tradersRes, itemsEnRes, tradersEnRes] = await Promise.all([
          fetch('https://json.tarkov.dev/pve/items'),
          fetch('https://json.tarkov.dev/pve/traders'),
          fetch('https://json.tarkov.dev/pve/items_en'),
          fetch('https://json.tarkov.dev/pve/traders_en')
        ]);
        
        const itemsJson = await itemsRes.json();
        const tradersJson = await tradersRes.json();
        const itemsEnJson = await itemsEnRes.json();
        const tradersEnJson = await tradersEnRes.json();
        
        const fetchedItemsData = itemsJson.data.items ? itemsJson.data.items : itemsJson.data;
        const fetchedTradersData = tradersJson.data.traders ? tradersJson.data.traders : tradersJson.data;
        const fetchedItemsEnData = itemsEnJson.data;
        const fetchedTradersEnData = tradersEnJson.data;

        Object.values(fetchedItemsData).forEach(item => {
            item.translatedName = fetchedItemsEnData[item.name] || item.name;
            item.translatedShortName = fetchedItemsEnData[item.shortName] || item.shortName;
        });

        setItemsData(fetchedItemsData);
        setTradersData(fetchedTradersData);
        setItemsEnData(fetchedItemsEnData);
        setTradersEnData(fetchedTradersEnData);
        
      } catch (error) {
        console.error("Connection failed.", error);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    localStorage.setItem('tarkovSavedItems', JSON.stringify(savedItems));
  }, [savedItems]);

  const getBestTraderInfo = (item) => {
    if (!item.sellToTrader || item.sellToTrader.length === 0) {
        return { price: 'N/A', name: 'No Vendor', rawPrice: 0 };
    }
    
    const bestSell = item.sellToTrader.reduce((prev, current) => 
        (prev.priceRUB > current.priceRUB) ? prev : current
    );
    
    const traderId = bestSell.trader;
    let traderName = 'Unknown Trader';
    
    if (tradersData[traderId] && tradersData[traderId].name) {
        const rawName = tradersData[traderId].name;
        traderName = tradersEnData[rawName] || rawName.replace(' Nickname', '');
    }
    
    return { 
        price: `${bestSell.price.toLocaleString()} ${bestSell.currency}`, 
        name: traderName,
        rawPrice: bestSell.priceRUB || 0
    };
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchInput(query);

    if (!query) {
        setSearchResults([]);
        return;
    }
    
    const results = Object.values(itemsData).filter(item => 
        (item.translatedName && item.translatedName.toLowerCase().includes(query)) || 
        (item.translatedShortName && item.translatedShortName.toLowerCase().includes(query))
    ).slice(0, 15);

    setSearchResults(results);
  };

  const saveItem = (item) => {
    if (!savedItems.some(saved => saved.id === item.id)) {
        const newSavedItems = [...savedItems, item];
        setSavedItems(newSavedItems);
        setSearchInput('');
        setSearchResults([]);
    }
  };

  const removeItem = (id) => {
    const newSavedItems = savedItems.filter(item => item.id !== id);
    setSavedItems(newSavedItems);
  };

  const sortTable = (column) => {
    let asc = true;
    if (currentSort.column === column) {
        asc = !currentSort.asc;
    }
    setCurrentSort({ column, asc });
    applySort(savedItems, column, asc);
  };

  const applySort = (items, column, asc) => {
    const sortedItems = [...items].sort((a, b) => {
        let valA, valB;
        if (column === 'name') {
            valA = a.translatedName || itemsEnData[a.name] || a.name || '';
            valB = b.translatedName || itemsEnData[b.name] || b.name || '';
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        } else if (column === 'vendor') {
            valA = getBestTraderInfo(a).name.toLowerCase();
            valB = getBestTraderInfo(b).name.toLowerCase();
        } else if (column === 'price') {
            valA = getBestTraderInfo(a).rawPrice;
            valB = getBestTraderInfo(b).rawPrice;
        }

        if (valA < valB) return asc ? -1 : 1;
        if (valA > valB) return asc ? 1 : -1;
        return 0;
    });
    setSavedItems(sortedItems);
  };

  const getSortIndicator = (column) => {
    if (currentSort.column === column) {
      return currentSort.asc ? '▲' : '▼';
    }
    return '';
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setSavedItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <div className="tracker-container">
      <h1>Flea Market & Vendor Intel</h1>
      <p>Search the live database to track best vendor sell prices and save items for your raid wishlist.</p>
      
      <div className="search-wrapper">
        <input 
          type="text" 
          id="searchInput" 
          placeholder={Object.keys(itemsData).length > 0 ? "Enter item name or designation..." : "Establishing connection to Traders..."} 
          disabled={Object.keys(itemsData).length === 0} 
          autoComplete="off"
          value={searchInput}
          onChange={handleSearchInputChange}
        />
        {searchResults.length > 0 && searchInput && (
          <div className="search-results">
            {searchResults.map(item => (
              <div key={item.id} className="search-result-item" onClick={() => saveItem(item)}>
                <img src={item.iconLink || ''} alt="" onError={(e) => e.target.style.display='none'} />
                <span>{item.translatedName}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="table-container" style={{ display: savedItems.length > 0 ? 'block' : 'none' }}>
        <table className="saved-table">
          <thead>
            <tr>
              <th className="td-icon"></th>
              <th className="sortable" onClick={() => sortTable('name')}>Item Designation <span className="sort-indicator">{getSortIndicator('name')}</span></th>
              <th className="sortable" onClick={() => sortTable('vendor')}>Optimal Vendor <span className="sort-indicator">{getSortIndicator('vendor')}</span></th>
              <th className="sortable" onClick={() => sortTable('price')}>Value <span className="sort-indicator">{getSortIndicator('price')}</span></th>
              <th className="td-action"></th>
            </tr>
          </thead>
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={savedItems.map(item => item.id)} 
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {savedItems.map((item, index) => (
                  <SortableRow 
                    key={item.id} 
                    item={item} 
                    index={index} 
                    getBestTraderInfo={getBestTraderInfo} 
                    itemsEnData={itemsEnData} 
                    removeItem={removeItem} 
                  />
                ))}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
    </div>
  );
}

export default TrackerContainer;